// src/users/index.js — екран „Потребители и достъп" + покана (Фаза 3.2).
// Пренесен дословно от app.js; PERM_MODULES живее тук (само този екран го ползва).

import { el, clear, $, field, input, initials, clickableRow } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { panelHead, searchInput, loadError } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { whenFmt, uaShort } from '../shared/audit.js';
import { state, activeCompanyObj } from '../shared/state.js';
import { MIN as PASS_MIN, passwordError } from '../../services/passwordPolicy.js';

// Контролируеми раздели и техните етикети (огледало на MODULES в auth.js).
const PERM_MODULES = [
  ['contacts', 'Контакти'], ['tasks', 'Задачи'],
  ['invoices', 'Фактури'], ['protocols', 'Протоколи'], ['checklist', 'Чек лист'],
  ['vehicles', 'Автопарк'], ['dossiers', 'Досиета'],
  // ПРОМТ-7: правата съществуват отсега. „Склад" има екран от ПРОМТ-8 (вижда
  // се при фирма със склад); „Доставки" — от ПРОМТ-9.
  ['warehouse', 'Склад'], ['purchases', 'Доставки'],
  // ПРОМТ-17: „Заплати" е право само за кантората — на клиентска роля
  // отметката не действа (и менюто, и сървърът отказват ролята client).
  ['zaplati', 'Заплати'],
];

function roleLabel(r) { return r === 'admin' ? 'Администратор' : r === 'client' ? 'Клиент' : 'Потребител'; }

function accessSummary(perms) {
  // Правата може да са и гранулирани ('invoices.read') — за резюмето ги
  // свеждаме до раздели (частта преди точката), без повторения; „само
  // преглед" личи с бележка до раздела.
  const list = perms || [];
  const mods = [];
  list.forEach((p) => {
    const m = String(p).split('.')[0];
    if (mods.indexOf(m) === -1) mods.push(m);
  });
  const map = Object.fromEntries(PERM_MODULES);
  const shown = mods.filter((m) => map[m]);
  if (!shown.length) return 'Без раздели';
  const readOnly = (m) => !list.includes(m) && !list.includes(m + '.edit') && list.includes(m + '.read');
  if (shown.length === PERM_MODULES.length && !shown.some(readOnly)) return 'Всички раздели';
  return shown.map((m) => map[m] + (readOnly(m) ? ' (преглед)' : '')).join(', ');
}

export async function view(page) {
  clear(page);
  let users = [];
  try { users = await api('/auth/users'); }
  catch (e) { page.appendChild(loadError(() => view(page))); return; }
  const panel = el('div', { class: 'panel' });
  panel.appendChild(panelHead('Потребители и достъп', 'Нов потребител', () => userForm()));
  const tb = el('tbody');
  const mkRow = (u) => clickableRow(() => userForm(u), 'Отвори потребител ' + u.name,
    el('td', {}, el('div', { style: 'display:flex;align-items:center;gap:10px' }, el('span', { class: 'avatar', text: initials(u.name) }), el('div', {}, el('div', { style: 'font-weight:600', text: u.name }), el('div', { style: 'font-size:13px;color:var(--muted)', text: u.email })))),
    el('td', {}, el('span', { class: 'tag ' + (u.role === 'admin' ? 'blue' : 'gray'), text: roleLabel(u.role) })),
    el('td', { class: 'hide-sm', style: 'font-size:13px;color:var(--muted)' }, u.role === 'admin' ? 'Пълен достъп' : accessSummary(u.permissions)),
    el('td', { class: 'row-actions', onClick: (e) => e.stopPropagation() }, u.id === state().user.id ? el('span', { class: 'tag gray', text: 'Вие' }) : el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('Да изтрия ли този потребител?', { okText: 'Изтрий', danger: true })) { try { await api('/auth/users/' + u.id, { method: 'DELETE' }); toast('Изтрито.'); view($('#page')); } catch (e) { toast('Изтриването не се получи. ' + e.message, true); } } } }))
  );
  const uFlt = { q: '', role: '' };
  const uSearch = searchInput({ placeholder: 'Търси име или имейл…', ariaLabel: 'Търсене на потребител по име или имейл', onSearch: (q) => { uFlt.q = q; uDraw(); } });
  const uRoleSel = el('select', { 'aria-label': 'Роля', onChange: (e) => { uFlt.role = e.target.value; uDraw(); } }, el('option', { value: '', text: 'Всички роли' }), el('option', { value: 'admin', text: 'Администратори' }), el('option', { value: 'user', text: 'Потребители' }), el('option', { value: 'client', text: 'Клиенти' }));
  const uCount = el('span', { style: 'color:var(--muted);font-size:13px;margin-left:auto' });
  panel.appendChild(el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:0 0 10px' }, uSearch, uRoleSel, uCount));
  const _roleKey = (u) => u.role === 'admin' ? 'admin' : (u.role === 'client' ? 'client' : 'user');
  const _roleOrder = ['admin', 'user', 'client'];
  const _roleTitle = { admin: 'Администратори', user: 'Потребители', client: 'Клиенти' };
  function uDraw() {
    clear(tb);
    const list = users.filter((u) => { if (uFlt.role && _roleKey(u) !== uFlt.role) return false; if (uFlt.q) { const hay = ((u.name || '') + ' ' + (u.email || '')).toLowerCase(); if (!hay.includes(uFlt.q)) return false; } return true; });
    list.sort((a, b) => (_roleOrder.indexOf(_roleKey(a)) - _roleOrder.indexOf(_roleKey(b))) || (a.name || '').localeCompare(b.name || ''));
    let _lastRole = null;
    list.forEach((u) => { const rk = _roleKey(u); if (rk !== _lastRole) { _lastRole = rk; tb.appendChild(el('tr', { class: 'month-row' }, el('td', { class: 'month-sep', colspan: 4 }, _roleTitle[rk] || rk))); } tb.appendChild(mkRow(u)); });
    uCount.textContent = list.length + ' от ' + users.length;
  }
  uDraw();
  panel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
    el('th', { scope: 'col', text: 'Потребител' }), el('th', { scope: 'col', text: 'Роля' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Достъп' }), el('th', { scope: 'col', text: '' })
  )), tb));
  page.appendChild(panel);

  const bkp = el('div', { class: 'panel', style: 'margin-top:16px' });
  bkp.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Резервно копие' })));
  bkp.appendChild(el('div', { style: 'padding:2px 2px 12px;color:var(--muted);font-size:14px;line-height:1.5' },
    el('div', { text: 'Системата прави автоматична снимка на данните всеки ден (пазят се последните 14 на сървъра).' }),
    el('div', { text: 'Натиснете бутона, за да изтеглите пълно копие на всички данни и да го съхраните извън сървъра.' })));
  bkp.appendChild(el('button', { class: 'btn sm', text: '⤓ Изтегли резервно копие', onClick: () => {
    const a = document.createElement('a'); a.href = '/api/backup/download'; document.body.appendChild(a); a.click(); a.remove();
    toast('Започва изтегляне…');
  } }));
  page.appendChild(bkp);

  const logPanel = el('div', { class: 'panel', style: 'margin-top:16px' });
  const logBody = el('div', {});
  const clearLogBtn = el('button', { class: 'btn sm danger', text: 'Изчисти лога', onClick: async () => {
    if (!(await confirmDialog('Да изтрия ли целия лог на влизанията? Действието е необратимо.', { okText: 'Изтрий', danger: true }))) return;
    try { await api('/auth/access-log', { method: 'DELETE' }); toast('Логът е изчистен.'); view($('#page')); } catch (e) { toast(e.message, true); }
  } });
  logPanel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Лог на влизанията' }), clearLogBtn));
  logPanel.appendChild(logBody);
  page.appendChild(logPanel);
  try {
    const logs = await api('/auth/access-log');
    if (!logs.length) logBody.appendChild(el('p', { class: 'empty', style: 'padding:14px', text: 'Още няма записани влизания.' }));
    else {
      const ltb = el('tbody');
      logs.forEach((l) => {
        const stt = l.status === 'failed' ? ['Неуспешно', 'red'] : l.status === 'blocked' ? ['Блокиран', 'amber'] : ['Успешно', 'green'];
        ltb.appendChild(el('tr', {},
          el('td', {}, el('span', { class: 'tag ' + stt[1], text: stt[0] })),
          el('td', {}, l.name || l.email || '—'),
          el('td', { class: 'hide-sm', style: 'color:var(--muted)' }, l.email || ''),
          el('td', {}, whenFmt(l.created_at)),
          el('td', { class: 'hide-sm', style: 'color:var(--muted)' }, l.ip || ''),
          el('td', { class: 'hide-sm', style: 'color:var(--muted)' }, uaShort(l.user_agent))
        ));
      });
      logBody.appendChild(el('table', {}, el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: 'Статус' }), el('th', { scope: 'col', text: 'Потребител' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Имейл' }), el('th', { scope: 'col', text: 'Дата и час' }), el('th', { scope: 'col', class: 'hide-sm', text: 'IP' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Устройство' })
      )), ltb));
    }
  } catch (e) { logBody.appendChild(el('p', { class: 'empty', style: 'padding:14px', text: 'Грешка при зареждане на лога.' })); }
}

function userForm(existing) {
  const u = existing || {};
  const isEdit = !!(existing && existing.id);
  const f = {
    name: input({ value: u.name || '', placeholder: 'Име' }),
    email: input({ type: 'email', value: u.email || '', placeholder: 'name@example.com' }),
    password: input({ type: 'password', placeholder: isEdit ? 'оставете празно за без промяна' : 'поне ' + PASS_MIN + ' знака' }),
    role: el('select', {},
      el('option', { value: 'user', text: 'Потребител', selected: (u.role || 'user') === 'user' }),
      el('option', { value: 'client', text: 'Клиент', selected: u.role === 'client' }),
      el('option', { value: 'admin', text: 'Администратор', selected: u.role === 'admin' })
    ),
  };
  if (isEdit) f.email.disabled = true;

  // Достъпът по раздел — три нива (грешка от картата, 16.10.2026: „Само
  // четене" съществуваше в сървъра, но не можеше да се зададе от екрана, а
  // запис на такъв потребител от формата му махаше раздела). Пълен = голото
  // право ('invoices'), само преглед = 'invoices.read'.
  const levelOf = (id) => {
    const perms = u.permissions || [];
    if (perms.includes(id) || perms.includes(id + '.edit')) return 'full';
    if (perms.includes(id + '.read')) return 'read';
    return '';
  };
  const checks = {};
  const checkWrap = el('div', { class: 'perm-grid' });
  PERM_MODULES.forEach(([id, label]) => {
    const sel = el('select', { 'aria-label': 'Достъп до ' + label },
      el('option', { value: '', text: '— няма —' }),
      el('option', { value: 'read', text: 'Само преглед' }),
      el('option', { value: 'full', text: 'Пълен достъп' }));
    sel.value = levelOf(id);
    const sync = () => { wrap.classList.toggle('on', !!sel.value); };
    sel.addEventListener('change', sync);
    checks[id] = sel;
    const wrap = el('label', { class: 'perm-row' }, el('span', { text: label }), sel);
    sync();
    checkWrap.appendChild(wrap);
  });
  const note = el('div', { style: 'font-size:13px;color:var(--muted);margin-top:6px' });
  const compChecks = {};
  const compWrap = el('div', { style: 'display:flex;flex-wrap:wrap;gap:10px' });
  (state().companies || []).forEach((co) => {
    const cb = el('input', { type: 'checkbox', checked: (u.companies == null) ? isEdit : (u.companies || []).includes(co.id) });
    compChecks[co.id] = cb;
    compWrap.appendChild(el('label', { style: 'display:flex;align-items:center;gap:6px;cursor:pointer;border:1px solid var(--border);border-radius:8px;padding:6px 10px' }, cb, el('span', { text: co.name })));
  });
  const compNote = el('div', { style: 'font-size:13px;color:var(--muted);margin-top:6px' });
  function syncRole() {
    const admin = f.role.value === 'admin';
    Object.values(checks).forEach((sel) => { sel.disabled = admin; });
    Object.values(compChecks).forEach((cb) => { cb.disabled = admin; });
    note.textContent = admin
      ? 'Администраторът има пълен достъп до всички раздели и управлява потребителите.'
      : 'За всеки раздел изберете: няма достъп, само преглед или пълен достъп. Изтриването остава за администратора.';
    compNote.textContent = admin
      ? 'Администраторът има достъп до всички фирми.'
      : 'Отметнете фирмите, до които този потребител има достъп.';
  }
  f.role.addEventListener('change', syncRole);
  syncRole();

  // (Г.8) Правилото за паролата беше само placeholder — изчезва още при
  // първия знак. Постоянен ред + жива проверка + око (както на входа).
  const eye = el('button', { class: 'pass-eye', type: 'button', 'aria-label': 'Покажи паролата', 'aria-pressed': 'false', text: '👁' });
  eye.addEventListener('click', () => {
    const show = f.password.type === 'password';
    f.password.type = show ? 'text' : 'password';
    eye.setAttribute('aria-pressed', String(show));
    eye.setAttribute('aria-label', show ? 'Скрий паролата' : 'Покажи паролата');
    f.password.focus();
  });
  const passWrap = el('div', { class: 'pass-wrap' }, f.password, eye);
  const passRule = el('div', { class: 'pass-rule cal-hint', style: 'padding:0 0 8px' });
  const syncPassRule = () => {
    const v = f.password.value;
    if (!v) { passRule.textContent = 'Паролата трябва да е поне ' + PASS_MIN + ' знака — например фраза от няколко думи.'; return; }
    const pe = passwordError(v, { email: f.email && f.email.value });
    passRule.textContent = !pe
      ? '✓ Паролата става (' + v.length + ' знака).'
      : (v.length < PASS_MIN ? 'Паролата трябва да е поне ' + PASS_MIN + ' знака — още ' + (PASS_MIN - v.length) + '.' : pe);
  };
  f.password.addEventListener('input', syncPassRule);
  syncPassRule();

  const body = el('div', {},
    field('Име', f.name),
    field('Имейл', f.email),
    field(isEdit ? 'Нова парола (по избор)' : 'Парола', passWrap),
    passRule,
    field('Роля', f.role),
    el('div', { class: 'field' }, el('label', { text: 'Достъп до раздели' }), checkWrap, note),
    el('div', { class: 'field' }, el('label', { text: 'Достъп до фирми' }), compWrap, compNote)
  );

  const save = async () => {
    const permissions = PERM_MODULES.filter(([id]) => checks[id].value).map(([id]) => (checks[id].value === 'read' ? id + '.read' : id));
    const companies = (state().companies || []).filter((co) => compChecks[co.id].checked).map((co) => co.id);
    try {
      let savedUser = null;
      if (isEdit) {
        const p = { name: f.name.value, role: f.role.value, permissions, companies };
        if (f.password.value) p.password = f.password.value;
        await api('/auth/users/' + existing.id, { method: 'PATCH', body: JSON.stringify(p) });
      } else {
        savedUser = await api('/auth/users', { method: 'POST', body: JSON.stringify({ name: f.name.value, email: f.email.value, password: f.password.value, role: f.role.value, permissions, companies }) });
      }
      closeModal(); toast('Записано.'); view($('#page'));
      if (savedUser) userInviteOverlay(savedUser, f.password.value);
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', {});
  if (isEdit && existing.id !== state().user.id) {
    foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('Да изтрия ли този потребител?', { okText: 'Изтрий', danger: true })) { try { await api('/auth/users/' + existing.id, { method: 'DELETE' }); closeModal(); toast('Изтрито.'); view($('#page')); } catch (e) { toast('Изтриването не се получи. ' + e.message, true); } } }, style: 'margin-right:auto' }));
  }
  if (isEdit) foot.appendChild(el('button', { class: 'btn sm ghost', text: 'Изпрати покана', onClick: () => userInviteOverlay(existing, f.password.value) }));
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: isEdit ? 'Запази' : 'Създай', onClick: save }));
  openModal({ title: isEdit ? 'Редакция на потребител' : 'Нов потребител', body, foot, wide: true });
}

// ---------------- Покана за нов потребител (линк по имейл) ----------------
function userInviteOverlay(user, password) {
  const link = window.location.origin;
  const co = activeCompanyObj();
  const linkInput = input({ value: link, readonly: 'readonly' });
  const to = input({ type: 'email', value: user.email || '' });
  const subject = input({ type: 'text', value: 'Покана за CRM' + (co && co.name ? (' на ' + co.name) : '') });
  const defaultBody = 'Здравейте, ' + (user.name || '') + ',\n\n'
    + 'Създаден е профил за Вас в CRM платформата.\n\n'
    + 'Вход: ' + link + '\n'
    + 'Потребител (имейл): ' + (user.email || '') + '\n'
    + (password ? ('Парола: ' + password + '\n') : '')
    + '\nПрепоръчваме да смените паролата след първо влизане.\n\nПоздрави.';
  const body = el('textarea', { style: 'min-height:160px' }, defaultBody);
  const status = el('div', { style: 'font-size:13px;min-height:18px;margin-top:4px' });
  const content = el('div', {},
    el('div', { class: 'field' }, el('label', { text: 'Линк към платформата' }),
      el('div', { style: 'display:flex;gap:6px' }, linkInput,
        el('button', { class: 'btn sm ghost', text: 'Копирай', onClick: () => { try { navigator.clipboard.writeText(link); toast('Линкът е копиран.'); } catch (e) { linkInput.select(); } } }))),
    field('До (имейл)', to),
    field('Тема', subject),
    field('Съобщение', body),
    status
  );
  const sendBtn = el('button', { class: 'btn', text: 'Изпрати' });
  sendBtn.addEventListener('click', async () => {
    const t = (to.value || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t)) { status.textContent = '✗ Въведете валиден имейл.'; status.style.color = '#b91c1c'; return; }
    sendBtn.disabled = true; const orig = sendBtn.textContent; sendBtn.textContent = 'Изпращане…'; status.textContent = '';
    try {
      await api('/auth/users/' + user.id + '/invite', { method: 'POST', body: JSON.stringify({ to: t, subject: subject.value, text: body.value }) });
      toast('Поканата е изпратена на ' + t + '.'); closeModal();
    } catch (e) { status.textContent = '✗ ' + e.message; status.style.color = '#b91c1c'; }
    finally { sendBtn.disabled = false; sendBtn.textContent = orig; }
  });
  const foot = el('div', {}, el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }), sendBtn);
  openModal({ title: 'Изпрати покана', body: content, foot, wide: true });
}

